import {
  Text,
  Paper,
  Group,
  useMantineTheme,
  PaperProps,
  Divider,
  Stack,
  useMantineColorScheme,
} from "@mantine/core"
import { IconCancel, IconChecks } from "@tabler/icons-react"
import { useGetAuthenticatedUser } from "../../app/-components/use-authenticated-user"
import { useBatchedSimpleUser } from "@/-dataloaders/simple-users"
import dayjs from "dayjs"
import { useDefaultAttachmentsApiForControllerRoute } from "../../../attachment-api"
import { FilePreview } from "../../../-components/FilePreview"
import { LinkifiedText } from "@components/LinkifiedText"
import { ChatPostDto } from "@/orval/gen/model"
import { useTranslation } from "react-i18next"

interface Props extends PaperProps {
  message: ChatPostDto
  showUserName?: boolean
  showReplyToMessage?: boolean
  showTimestamp?: boolean
  onReplyButtonClick?: () => void
  showReadIndicator: boolean
}

export function ChatMessagePaper({
  message,
  showReplyToMessage,
  showUserName,
  showReadIndicator,
  showTimestamp,
  ...props
}: Props) {
  const theme = useMantineTheme()
  const { data: authUser } = useGetAuthenticatedUser()
  const { data: user } = useBatchedSimpleUser(message.userId)

  const isOwnMessage = authUser.userId === message.userId
  const { colorScheme } = useMantineColorScheme()

  const bg = React.useMemo(
    () =>
      colorScheme === "dark"
        ? isOwnMessage
          ? "blue.8"
          : "gray.8"
        : isOwnMessage
          ? "blue.2"
          : "gray.2",
    [colorScheme, isOwnMessage]
  )

  const textColor = undefined

  const { getFileUrl } = useDefaultAttachmentsApiForControllerRoute("chatpost")

  const { t } = useTranslation("app_server")

  return (
    <Paper
      shadow="xs"
      p={5}
      bg={bg}
      display="flex"
      style={{
        flexDirection: isOwnMessage ? "row-reverse" : "row",
        alignItems: "center",
      }}
      {...props}
    >
      <Stack lh={1.25} w={"100%"} justify="flex-end" gap={1}>
        {showUserName && !isOwnMessage ? (
          <Text c={textColor} fw={600}>
            {user?.userName ?? message.userId}
          </Text>
        ) : null}
        {message.replyToMessage && showReplyToMessage ? (
          <ChatMessagePaper
            showUserName
            showReadIndicator={false}
            withBorder
            shadow="unset"
            flex="1 0 0"
            message={message.replyToMessage}
            fz={"xs"}
          />
        ) : null}
        {message.attachments.length > 0 && (
          <>
            <Group py={"xs"} justify="flex-end">
              {message.attachments.map((attachment) => (
                <FilePreview
                  bg={"var(--mantine-color-body)"}
                  key={attachment.attachmentId}
                  bdrs={5}
                  p={"xs"}
                  mah={400}
                  w={400}
                  maw={"100%"}
                  src={`${window.location.origin}${getFileUrl(
                    attachment.attachmentId,
                    message.chatPostId
                  )}`}
                  fileName={attachment.fileName}
                />
              ))}
            </Group>
            <Divider />
          </>
        )}
        {message.isDeleted ? (
          <Text fs={"italic"} size="xs">
            <IconCancel size={12} style={{ verticalAlign: "text-bottom" }} />{" "}
            {isOwnMessage
              ? t("Chat_OwnMessageDeleted")
              : t("Chat_MessageDeleted")}
          </Text>
        ) : (
          <LinkifiedText
            inherit
            c={textColor}
            style={{
              overflowWrap: "break-word",
              userSelect: "text",
              whiteSpace: "pre-wrap",
            }}
            content={message.postText}
          />
        )}
        <Group style={{ alignSelf: "end" }} gap={5}>
          {showReadIndicator && (
            <IconChecks
              size="16"
              style={{ verticalAlign: "text-bottom" }}
              color={message.isRead ? theme.colors.green[4] : "gray"}
            />
          )}
          {message.isEdited && !message.isDeleted && (
            <Text size="xs">{t("Edited")}</Text>
          )}
          {showTimestamp && (
            <Text c={textColor} lh={1} size="xs">
              {dayjs(message.timeStamp).format("HH:mm")}
            </Text>
          )}
        </Group>
      </Stack>
    </Paper>
  )
}
